/**
 * Thin browser binding for Flux Agent live voice (OpenAI GPT-Live over WebRTC).
 *
 * All decisions live in Rust: the Worker builds the session, holds the API key, and its
 * `LiveSessionRoom` runs tools and stores the transcript through a sideband connection. This
 * module only does what must happen in the browser:
 *
 * 1. open the microphone (echo cancellation, noise suppression, auto gain) and a
 *    `RTCPeerConnection` with the `oai-events` data channel, created before the offer;
 * 2. post the complete SDP offer to `POST /flux-agent/conversations/:id/live/session` and apply
 *    the returned answer;
 * 3. play GPT-Live's audio track, and turn transcript deltas from the data channel into
 *    captions (display only: the server stores the authoritative transcript);
 * 4. on stop, send `session.close` and wait for `session.closed` before releasing the
 *    microphone, then signal the conversation end so the energetic summary runs;
 * 5. reconnect once if the connection drops mid-session.
 *
 * {@link isLiveFallback} tells the caller when to use the older transcribe → chat → speak
 * pipeline instead (no WebRTC, live voice not configured, OpenAI unavailable).
 *
 * Prosody is not captured here: the AudioWorklet prosody analyzer lives on another branch and
 * prosody reaches the server inside typed-message bodies, while live voice stores messages
 * server-side. Wiring it needs a prosody field on the live session route; see the developer guide.
 *
 * @module apps/web/src/lib/fluxAgentLive
 */

import { API_BASE, request } from '@/lib/api';
import { currentTimeZone, toFluxAgentResult, type FluxAgentResult } from '@/lib/fluxAgentApi';

/** Lifecycle of one live voice conversation. */
export type LivePhase = 'idle' | 'connecting' | 'live' | 'reconnecting' | 'closing' | 'closed' | 'failed';

/** Who said a caption. */
export type LiveSpeaker = 'user' | 'assistant';

/** One caption bubble: consecutive transcript fragments of one speaker. */
export interface LiveCaption {
  /** Speaker of every fragment in the bubble. */
  speaker: LiveSpeaker;
  /** Fragments concatenated exactly as received. */
  text: string;
}

/** Latency of the connection steps, in milliseconds from {@link FluxAgentLiveSession.start}. */
export interface LiveTimings {
  /** Microphone opened and the SDP offer gathered. */
  offerMs?: number;
  /** The Worker answered with the SDP answer (includes OpenAI session creation). */
  answerMs?: number;
  /** `session.started` arrived on the data channel. */
  startedMs?: number;
  /** The first non-silent audio from GPT-Live arrived (time to first audio). */
  firstAudioMs?: number;
  /** Server-side split of the answer step, as reported by the route. */
  server?: {
    /** Prompt and history assembly. */
    promptMs: number;
    /** `POST /v1/live/sessions` at OpenAI. */
    createMs: number;
    /** Sideband attach by the live room. */
    attachMs: number;
  };
}

/** Successful body of the session route. */
export interface LiveSessionStart {
  /** Opaque OpenAI session id. */
  sessionId: string;
  /** SDP answer to apply as the remote description. */
  sdp: string;
  /** GPT-Live model in use. */
  model: string;
  /** Server-enforced session cap in seconds. */
  maxSeconds: number;
  /** Server-side timing split. */
  timings: {
    /** Prompt and history assembly. */
    promptMs: number;
    /** OpenAI session creation. */
    createMs: number;
    /** Sideband attach. */
    attachMs: number;
  };
}

/** Body posted to the session route. */
export interface LiveStartBody {
  /** Complete SDP offer, ICE candidates included. */
  sdp: string;
  /** IANA timezone for the prompt clock. */
  timezone: string;
  /** Ask GPT-Live to greet first; false on a reconnect. */
  greet: boolean;
}

/** Result of {@link FluxAgentLiveSession.start}. */
export type LiveStartOutcome =
  | {
      /** The answer was applied; `session.started` follows on the data channel. */
      ok: true;
    }
  | {
      /** The session could not start. */
      ok: false;
      /** True when the older voice pipeline should take over. */
      fallback: boolean;
      /** User-presentable reason. */
      message: string;
    };

/** The parts of a failed route result that decide the fallback. */
export interface LiveFailure {
  /** HTTP status, when a response arrived. */
  status?: number;
  /** Server error code. */
  code: string;
}

/** Browser capabilities and network calls, injectable for tests. */
export interface LiveDeps {
  /** Create a peer connection. */
  createPeer: () => RTCPeerConnection;
  /** Open the microphone. */
  getUserMedia: (constraints: MediaStreamConstraints) => Promise<MediaStream>;
  /** Exchange the offer for an answer through the Worker. */
  startSession: (
    conversationId: string,
    body: LiveStartBody
  ) => Promise<FluxAgentResult<LiveSessionStart>>;
  /** Signal the end of the conversation segment (fire-and-forget). */
  endConversation: (conversationId: string) => void;
  /** Create the element that plays GPT-Live's audio. */
  createAudio: () => HTMLAudioElement;
  /** Monotonic clock in milliseconds. */
  now: () => number;
}

/** Callbacks for UI state. */
export interface LiveCallbacks {
  /** Phase changed. */
  onPhase?: (phase: LivePhase) => void;
  /** Captions changed (a new array each time). */
  onCaptions?: (captions: LiveCaption[]) => void;
  /** A timing was measured. */
  onTimings?: (timings: LiveTimings) => void;
  /** A user-presentable problem; `fallback` means the old voice pipeline should take over. */
  onError?: (message: string, fallback: boolean) => void;
  /** The session finished, with final billed seconds when `session.closed` confirmed them. */
  onClosed?: (usageSeconds: number | null) => void;
}

/** Options for one live conversation. */
export interface LiveOptions extends LiveCallbacks {
  /** Conversation the voice exchange is stored in. */
  conversationId: string;
  /** Ask GPT-Live to greet the person first (default true). */
  greet?: boolean;
  /** Reconnects allowed after an unexpected drop (default 1). */
  maxReconnects?: number;
  /** How long to wait for `session.closed` after `session.close`, in ms (default 8000). */
  closeTimeoutMs?: number;
}

/** Error codes from the session route that mean "use the older voice pipeline". */
const FALLBACK_CODES = new Set(['FLUX_AGENT_LIVE_UNAVAILABLE', 'FLUX_AGENT_NOT_CONFIGURED']);

/** Whether a failed session start should hand over to the transcribe → chat → speak pipeline. */
export function isLiveFallback(result: LiveFailure): boolean {
  return FALLBACK_CODES.has(result.code) || result.status === 503 || result.status === 501;
}

/** The globals `canRunLiveVoice` probes; tests pass a plain object in place of `globalThis`. */
export interface LiveVoiceScope {
  /** WebRTC peer constructor; must be a function. */
  RTCPeerConnection?: unknown;
  /** Browser navigator; `mediaDevices.getUserMedia` must be a function. */
  navigator?: {
    /** Media capture entry point. */
    mediaDevices?: {
      /** Microphone capture function. */
      getUserMedia?: unknown;
    };
  };
}

/** Whether this browser can run live voice at all (WebRTC plus microphone capture). */
export function canRunLiveVoice(scope: LiveVoiceScope = globalThis): boolean {
  return (
    typeof scope.RTCPeerConnection === 'function' &&
    typeof scope.navigator?.mediaDevices?.getUserMedia === 'function'
  );
}

/** Append a transcript fragment to the caption list, starting a new bubble on a speaker change. */
export function appendCaption(
  captions: LiveCaption[],
  speaker: LiveSpeaker,
  delta: string
): LiveCaption[] {
  const last = captions[captions.length - 1];
  if (last && last.speaker === speaker)
    return [...captions.slice(0, -1), { speaker, text: last.text + delta }];
  if (!delta.trim()) return captions;
  return [...captions, { speaker, text: delta.trimStart() }];
}

/** Default dependencies backed by the real browser and API. */
export function browserDeps(): LiveDeps {
  return {
    createPeer: () => new RTCPeerConnection(),
    getUserMedia: constraints => navigator.mediaDevices.getUserMedia(constraints),
    startSession: async (conversationId, body) =>
      toFluxAgentResult(
        await request<LiveSessionStart>(
          `/flux-agent/conversations/${encodeURIComponent(conversationId)}/live/session`,
          { method: 'POST', body: JSON.stringify(body) }
        )
      ),
    endConversation: conversationId => {
      // Keep-alive fetch (a beacon cannot carry the CSRF header). A 404 on deployments without
      // the end route is harmless: the idle sweep produces the summary later.
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        'X-Fluxability-CSRF': '1',
      };
      try {
        const token = localStorage.getItem('flexiplanner-session-token');
        if (token) headers.Authorization = `Bearer ${token}`;
      } catch {
        // Cookie authentication still applies.
      }
      void fetch(`${API_BASE}/flux-agent/conversations/${encodeURIComponent(conversationId)}/end`, {
        method: 'POST',
        body: '{}',
        headers,
        credentials: 'include',
        keepalive: true,
      }).catch(() => undefined);
    },
    createAudio: () => {
      const audio = new Audio();
      audio.autoplay = true;
      return audio;
    },
    now: () => performance.now(),
  };
}

/** Wait until ICE gathering completes (or `timeoutMs`), so the offer carries its candidates. */
function gathered(peer: RTCPeerConnection, timeoutMs = 3000): Promise<void> {
  if (peer.iceGatheringState === 'complete') return Promise.resolve();
  return new Promise(resolve => {
    const timer = setTimeout(done, timeoutMs);
    function done() {
      clearTimeout(timer);
      peer.removeEventListener('icegatheringstatechange', check);
      resolve();
    }
    function check() {
      if (peer.iceGatheringState === 'complete') done();
    }
    peer.addEventListener('icegatheringstatechange', check);
  });
}

/**
 * One parsed data-channel message. The JSON is unchecked, so each field is optional and read
 * defensively; only captions and lifecycle events are acted on.
 */
interface LiveChannelEvent {
  /** Event name such as `session.started` or `session.output_transcript.delta`. */
  type?: string;
  /** Caption fragment of a transcript delta; coerced to a string. */
  delta?: unknown;
  /** Usage reported when the session closes. */
  usage?: {
    /** Billed live seconds, used only when it is a number. */
    seconds?: unknown;
  };
  /** Error detail; `session_expired` ends the session quietly. */
  error?: {
    /** Machine-readable error code. */
    code?: unknown;
  };
}

/**
 * One live voice conversation. Create it from a user gesture (iOS unlocks audio only there),
 * call {@link start}, and {@link stop} to finish. Not reusable after it closed.
 */
export class FluxAgentLiveSession {
  /** Current phase. */
  phase: LivePhase = 'idle';
  /** Captions so far. */
  captions: LiveCaption[] = [];
  /** Measured timings. */
  timings: LiveTimings = {};
  /** Options and callbacks. */
  private readonly options: LiveOptions;
  /** Injected browser and network dependencies. */
  private readonly deps: LiveDeps;
  /** Active peer connection. */
  private peer: RTCPeerConnection | null = null;
  /** Active `oai-events` channel. */
  private channel: RTCDataChannel | null = null;
  /** Open microphone stream. */
  private microphone: MediaStream | null = null;
  /** Element playing GPT-Live's audio. */
  private audio: HTMLAudioElement | null = null;
  /** Clock value when {@link start} was called. */
  private startedAt = 0;
  /** Reconnects used so far. */
  private reconnects = 0;
  /** Resolves the pending close wait when `session.closed` arrives. */
  private closedWaiter: (() => void) | null = null;
  /** Polls inbound audio energy until the first audio arrives. */
  private audioProbe: ReturnType<typeof setInterval> | null = null;
  /** Set once {@link stop} began, so drops are not treated as failures. */
  private stopping = false;

  /** Create a session with optional injected dependencies (tests). */
  constructor(options: LiveOptions, deps: LiveDeps = browserDeps()) {
    this.options = options;
    this.deps = deps;
  }

  /**
   * Connect. Resolves `{ok: true}` once the answer is applied, or `{ok: false, fallback}` when
   * the session could not start; with `fallback` the caller should start the older pipeline.
   */
  async start(): Promise<LiveStartOutcome> {
    if (this.phase !== 'idle') return { ok: true };
    this.startedAt = this.deps.now();
    this.setPhase('connecting');
    const outcome = await this.connect(this.options.greet ?? true);
    if (!outcome.ok) {
      this.setPhase('failed');
      this.options.onError?.(outcome.message, outcome.fallback);
    }
    return outcome;
  }

  /** Open media, exchange the offer and apply the answer. */
  private async connect(greet: boolean): Promise<LiveStartOutcome> {
    let peer: RTCPeerConnection;
    try {
      this.microphone ??= await this.deps.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
      });
      peer = this.deps.createPeer();
    } catch (error) {
      const denied = error instanceof Error && /denied|NotAllowed/i.test(`${error.name} ${error.message}`);
      return {
        ok: false,
        fallback: !denied,
        message: denied
          ? 'Microphone access is blocked. Allow it in your browser to talk to Flux Agent.'
          : 'Live voice is not supported in this browser.',
      };
    }
    this.peer = peer;
    this.audio ??= this.deps.createAudio();
    peer.addEventListener('track', event => {
      if (!this.audio) return;
      this.audio.srcObject = event.streams[0] ?? new MediaStream([event.track]);
      void this.audio.play?.()?.catch?.(() => undefined);
    });
    for (const track of this.microphone.getAudioTracks()) peer.addTrack(track, this.microphone);
    const channel = peer.createDataChannel('oai-events');
    this.channel = channel;
    channel.addEventListener('message', event => this.onEvent(event.data));
    channel.addEventListener('close', () => this.onDrop(channel));
    peer.addEventListener('connectionstatechange', () => {
      if (peer.connectionState === 'failed') this.onDrop(channel);
    });
    try {
      await peer.setLocalDescription(await peer.createOffer());
      await gathered(peer);
    } catch {
      this.teardownPeer();
      return { ok: false, fallback: true, message: 'Live voice could not start a connection.' };
    }
    this.mark('offerMs');
    const result = await this.deps.startSession(this.options.conversationId, {
      sdp: peer.localDescription?.sdp ?? '',
      timezone: currentTimeZone(),
      greet,
    });
    if (!result.ok) {
      this.teardownPeer();
      return { ok: false, fallback: isLiveFallback(result), message: result.message };
    }
    this.mark('answerMs');
    this.timings = { ...this.timings, server: result.data.timings };
    try {
      await peer.setRemoteDescription({ type: 'answer', sdp: result.data.sdp });
    } catch {
      this.teardownPeer();
      return { ok: false, fallback: true, message: 'Live voice could not connect.' };
    }
    this.probeFirstAudio(peer);
    return { ok: true };
  }

  /** Record a timing relative to {@link start} and report it. */
  private mark(key: 'offerMs' | 'answerMs' | 'startedMs' | 'firstAudioMs') {
    if (this.timings[key] !== undefined) return;
    this.timings = { ...this.timings, [key]: Math.round(this.deps.now() - this.startedAt) };
    this.options.onTimings?.(this.timings);
  }

  /** Poll inbound audio energy (100 ms) until GPT-Live's first non-silent audio arrives. */
  private probeFirstAudio(peer: RTCPeerConnection) {
    if (this.timings.firstAudioMs !== undefined || typeof peer.getStats !== 'function') return;
    this.audioProbe = setInterval(async () => {
      try {
        const stats = await peer.getStats();
        stats.forEach(
          (report: {
            /** Stats entry type, `inbound-rtp` for received media. */ type?: string;
            /** Media kind of an RTP entry. */ kind?: string;
            /** Cumulative received audio energy; positive once sound arrives. */ totalAudioEnergy?: number;
          }) => {
            if (
              report.type === 'inbound-rtp' &&
              report.kind === 'audio' &&
              (report.totalAudioEnergy ?? 0) > 0
            )
              this.mark('firstAudioMs');
          }
        );
      } catch {
        // Stats are best-effort.
      }
      if (this.timings.firstAudioMs !== undefined && this.audioProbe) {
        clearInterval(this.audioProbe);
        this.audioProbe = null;
      }
    }, 100);
  }

  /** Handle one data-channel event; only captions and lifecycle are used here. */
  private onEvent(data: unknown) {
    let event: LiveChannelEvent | null;
    try {
      event = JSON.parse(String(data)) as LiveChannelEvent | null;
    } catch {
      return;
    }
    switch (event?.type) {
      case 'session.started':
        this.mark('startedMs');
        this.reconnects = 0;
        this.setPhase('live');
        break;
      case 'session.input_transcript.delta':
        this.caption('user', String(event.delta ?? ''));
        break;
      case 'session.output_transcript.delta':
        this.caption('assistant', String(event.delta ?? ''));
        break;
      case 'session.closed':
        this.finish(typeof event.usage?.seconds === 'number' ? event.usage.seconds : null);
        break;
      case 'error':
        if (event.error?.code === 'session_expired') this.finish(null);
        break;
    }
  }

  /** Add a caption fragment and notify. */
  private caption(speaker: LiveSpeaker, delta: string) {
    this.captions = appendCaption(this.captions, speaker, delta);
    this.options.onCaptions?.(this.captions);
  }

  /** The channel or connection dropped: reconnect once while live, else fail. */
  private onDrop(channel: RTCDataChannel) {
    if (channel !== this.channel || this.stopping || this.phase === 'closed') return;
    if (this.closedWaiter) return this.finish(null);
    this.teardownPeer();
    if (this.phase === 'live' && this.reconnects < (this.options.maxReconnects ?? 1)) {
      this.reconnects += 1;
      this.setPhase('reconnecting');
      void this.connect(false).then(outcome => {
        if (!outcome.ok) {
          this.options.onError?.(outcome.message, outcome.fallback);
          this.finish(null);
        }
      });
      return;
    }
    this.options.onError?.('The live voice connection dropped.', false);
    this.finish(null);
  }

  /**
   * End the conversation: send `session.close`, wait for `session.closed` (or the timeout) so
   * pending work drains and usage is final, then release the microphone and signal the end.
   */
  async stop(): Promise<void> {
    if (this.phase === 'closed' || this.phase === 'idle' || this.stopping) return;
    this.stopping = true;
    const channel = this.channel;
    if (channel?.readyState === 'open') {
      this.setPhase('closing');
      const closed = new Promise<void>(resolve => (this.closedWaiter = resolve));
      channel.send(JSON.stringify({ type: 'session.close' }));
      const timeout = new Promise<void>(resolve =>
        setTimeout(resolve, this.options.closeTimeoutMs ?? 8000)
      );
      await Promise.race([closed, timeout]);
    }
    this.finish(null);
  }

  /** Mute or unmute the microphone locally (the session keeps running). */
  setMuted(muted: boolean) {
    for (const track of this.microphone?.getAudioTracks() ?? []) track.enabled = !muted;
  }

  /** Close the peer connection and its channel, keeping the microphone for a reconnect. */
  private teardownPeer() {
    if (this.audioProbe) clearInterval(this.audioProbe);
    this.audioProbe = null;
    const channel = this.channel;
    this.channel = null;
    try {
      channel?.close();
    } catch {
      // Already closed.
    }
    this.peer?.close();
    this.peer = null;
  }

  /** Release everything once, report usage, and signal the conversation end. */
  private finish(usageSeconds: number | null) {
    const waiter = this.closedWaiter;
    this.closedWaiter = null;
    waiter?.();
    if (this.phase === 'closed') return;
    this.teardownPeer();
    for (const track of this.microphone?.getTracks() ?? []) track.stop();
    this.microphone = null;
    if (this.audio) this.audio.srcObject = null;
    this.setPhase('closed');
    this.options.onClosed?.(usageSeconds);
    this.deps.endConversation(this.options.conversationId);
  }

  /** Update the phase and notify. */
  private setPhase(phase: LivePhase) {
    if (this.phase === phase) return;
    this.phase = phase;
    this.options.onPhase?.(phase);
  }
}
